import type { BiOverview } from '../../api/bridge'

/**
 * Ressalvas da margem de contribuição na Home.
 *
 * Por que isto existe: a MC do período é somada a partir de `calculated_margin`,
 * que em parte dos pedidos foi gravada com custo faltando. Mostrar "MC 24%" sem
 * dizer isso é entregar um número inflado como se fosse resultado.
 *
 * ⚠️ O que este módulo NÃO consegue ver. O painel "Margem por Anúncio" detecta,
 * indo direto ao Mongo, dois problemas que o bridge não expõe em endpoint nenhum:
 *
 *   - `shipping_cost` NEGATIVO (66 pedidos do ML em 16/09/2026, somando na margem
 *     em vez de subtrair)
 *   - `marketplace_fees` zerado na Amazon (306 de 306 pedidos), que joga a MC dela
 *     para ~42%
 *
 * Nenhum dos dois é derivável dos agregados que chegam aqui. Por isso a tela
 * remete a Margens em vez de afirmar um número de pedidos afetados que ela não
 * tem como contar — inventar essa contagem seria pior que não mostrar.
 */

export type Ressalva = {
  /** Texto curto do selo, ao lado do número. */
  selo: string
  /** Frase completa, no tooltip. */
  detalhe: string
  nivel: 'aviso' | 'grave'
}

export function ressalvasDaMargem(data: BiOverview | null): Ressalva[] {
  if (!data) return []
  const out: Ressalva[] = []

  if (data.cmvInconsistente) {
    out.push({
      nivel: 'grave',
      selo: 'CMV fora de escala',
      detalhe:
        `O CMV somado (${data.cmvFmt}) está fora de proporção com as vendas do período. ` +
        'Isso é cadastro de custo, não resultado — a margem abaixo não deve ser lida como P&L.',
    })
  }

  const { totalMargens, pedidos } = data

  if (pedidos > 0 && totalMargens === 0) {
    out.push({
      nivel: 'grave',
      selo: 'Sem cobertura',
      detalhe:
        `Nenhum dos ${pedidos} pedidos do período tem margem calculada. ` +
        'O valor ao lado não representa o período.',
    })
  } else if (pedidos > 0 && totalMargens > 0 && totalMargens < pedidos) {
    const pct = (totalMargens / pedidos) * 100
    out.push({
      // Abaixo de 80% de cobertura a amostra deixa de ser representativa do período.
      nivel: pct < 80 ? 'grave' : 'aviso',
      selo: `Amostra ${totalMargens}/${pedidos}`,
      detalhe:
        `Só ${totalMargens} dos ${pedidos} pedidos têm margem calculada ` +
        `(${pct.toFixed(0)}% de cobertura). A MC ao lado é desses pedidos, não do período inteiro.`,
    })
  }

  // Aviso de fundo: vale sempre que há margem na tela, porque o problema é da
  // base e não do recorte. Fica por último, com o peso mais leve.
  if (totalMargens > 0) {
    out.push({
      nivel: 'aviso',
      selo: 'Custos incompletos',
      detalhe:
        'Parte dos pedidos está gravada com frete ou comissão faltando, o que deixa a margem ' +
        'maior do que é. No Mercado Livre a MC dos pedidos completos fica perto de 14%, contra ' +
        '~24% do conjunto todo. Ver o detalhe em Margens.',
    })
  }

  return out
}
